// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pictures, sound, video and fonts an HTML page loads from the web, in its markup
// (`<img src="https://…">`, srcset, posters, `<audio>`/`<video>` and their `<source>`s, SVG
// `<image>`, CSS `url()` in `style` and `<style>`, `background=`) or set by its script as it runs
// (`img.src = LIB + path`, `innerHTML` with pictures, `new Audio(url)`, `el.style.backgroundImage`).
// The preview's policy blocks every web address, so each comes through Konvo's media proxy instead:
// written into the page where the page names it, and asked for over a private channel as the
// page's script sets the rest. The host never sees the viewer's address.
//
// Where pages are an agent's own (Agents), the source is set once for everything shown there
// (WebPicturesContext), so every way an HTML page shows (a reply's card, a page the agent showed
// with html_render, an ```html block, a file opened from Files) has it, and a new one does too.

import type React from 'react';
import {createContext, useContext, useEffect} from 'react';

/** Gives media proxy addresses for the web files a page loads, for pages whose source allows it. */
export interface WebPictures {
	/** Each address as its media proxy address; ones that can't be had are left out. */
	resolve: (urls: Array<string>, signal: AbortSignal) => Promise<Record<string, string>>;
	/** Where every address it gives starts (the media proxy), for the page's policy. */
	readonly base: string;
}

/**
 * The web pictures source HTML pages below inherit when they aren't given one. Unset (the default),
 * web addresses stay blocked, as for pages people send in chats.
 */
export const WebPicturesContext = createContext<WebPictures | undefined>(undefined);

/** The source given, else the one inherited (WebPicturesContext). */
export function useWebPicturesSource(given?: WebPictures): WebPictures | undefined {
	const inherited = useContext(WebPicturesContext);
	return given ?? inherited;
}

/** Web files one page may load. */
export const WEB_PICTURES_PER_PAGE = 400;
const WEB_PICTURE_URL_MAX = 2048;
/** How long the frame's requests gather before they go together. */
const GATHER_MS = 30;
/** Places the page's policy admits as they are (Google Fonts), so they aren't proxied. */
const ADMITTED = /^https:\/\/fonts\.(googleapis|gstatic)\.com\//i;
/** CSS `url(…)` with a web address; `@import`ed stylesheets aren't pictures and stay out. */
const CSS_URL = /(@import\s+)?url\(\s*(['"]?)(https?:\/\/[^'")\s]+)\2\s*\)/gi;

/** A web picture's address (http or https), else null. */
export function webPictureUrl(raw: string): string | null {
	const value = raw.trim();
	return /^https?:\/\/[^\s]/i.test(value) && value.length <= WEB_PICTURE_URL_MAX ? value : null;
}

/** The media proxy base as a policy source (`https://host/media/`), or null when it can't be one. */
export function webPictureSource(base: string): string | null {
	try {
		const url = new URL(base);
		if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
		const source = `${url.origin}${url.pathname.replace(/\/?$/, '/')}`;
		return /[\s;,'"]/.test(source) ? null : source;
	} catch {
		return null;
	}
}

/** The attributes that name one file an element shows or plays. */
function fileAttributes(element: Element): Array<string> {
	switch (element.localName) {
		case 'img':
		case 'audio':
		case 'source':
			return ['src'];
		case 'input':
			return element.getAttribute('type')?.toLowerCase() === 'image' ? ['src'] : [];
		case 'video':
			return ['src', 'poster'];
		case 'image':
			return ['href', 'xlink:href'];
		case 'body':
		case 'table':
		case 'tr':
		case 'td':
		case 'th':
			return ['background'];
		default:
			return [];
	}
}

/** A srcset's candidates: each address with its descriptor (`2x`, `640w`, or none). */
export function srcsetCandidates(value: string): Array<[string, string]> {
	const out: Array<[string, string]> = [];
	let at = 0;
	while (at < value.length) {
		while (at < value.length && /[\s,]/.test(value[at]!)) at++;
		if (at >= value.length) break;
		let start = at;
		while (at < value.length && !/\s/.test(value[at]!)) at++;
		let url = value.slice(start, at);
		let descriptor = '';
		if (url.endsWith(',')) url = url.replace(/,+$/, '');
		else {
			start = at;
			while (at < value.length && value[at] !== ',') at++;
			descriptor = value.slice(start, at).trim();
		}
		out.push([url, descriptor]);
	}
	return out;
}

/**
 * The page with each web file it names in its markup (and that the source can give) as its media
 * proxy address. With `live`, a script goes along that does the same for what the page's own
 * script sets later (webPictureScript).
 */
export async function prepareWebPictures(
	html: string,
	pictures: WebPictures,
	live: boolean,
	signal: AbortSignal,
): Promise<string> {
	const proxiable = (raw: string | null) => {
		const url = webPictureUrl(raw ?? '');
		return url && !url.startsWith(pictures.base) && !ADMITTED.test(url) ? url : null;
	};
	const cssUrls = (css: string) =>
		[...css.matchAll(CSS_URL)].flatMap((match) => (match[1] ? [] : [proxiable(match[3]!)].filter(Boolean)));
	const doc = new DOMParser().parseFromString(html, 'text/html');
	const found = new Set<string>();
	const add = (url: string | null) => {
		if (url && (found.has(url) || found.size < WEB_PICTURES_PER_PAGE)) found.add(url);
	};
	const elements = [...doc.querySelectorAll('*')];
	for (const element of elements) {
		for (const name of fileAttributes(element)) add(proxiable(element.getAttribute(name)));
		if (element.hasAttribute('srcset'))
			for (const [url] of srcsetCandidates(element.getAttribute('srcset')!)) add(proxiable(url));
		if (element.hasAttribute('style')) cssUrls(element.getAttribute('style')!).forEach(add);
		if (element.localName === 'style') cssUrls(element.textContent ?? '').forEach(add);
	}
	if (!found.size && !live) return html;
	const resolved = found.size
		? await pictures.resolve([...found], signal).catch(() => ({}) as Record<string, string>)
		: {};
	const links: Record<string, string> = Object.fromEntries(
		Object.entries(resolved).filter(([, proxied]) => proxied.startsWith(pictures.base)),
	);
	const swapCss = (css: string) =>
		css.replace(CSS_URL, (match, imported, _quote, url: string) =>
			!imported && links[url] ? `url("${links[url]}")` : match,
		);
	for (const element of elements) {
		for (const name of fileAttributes(element)) {
			const url = proxiable(element.getAttribute(name));
			if (url && links[url]) element.setAttribute(name, links[url]);
		}
		const srcset = element.getAttribute('srcset');
		if (srcset !== null) {
			// The browser picks from srcset over src, and the policy blocks the web ones that can't be had.
			const kept = srcsetCandidates(srcset).flatMap(([url, descriptor]) => {
				const web = proxiable(url);
				const address = web ? links[web] : url;
				return address ? [descriptor ? `${address} ${descriptor}` : address] : [];
			});
			if (kept.length) element.setAttribute('srcset', kept.join(', '));
			else if (element.localName === 'source') element.remove();
			else element.removeAttribute('srcset');
		}
		const style = element.getAttribute('style');
		if (style) element.setAttribute('style', swapCss(style));
		if (element.localName === 'style' && element.textContent) element.textContent = swapCss(element.textContent);
	}
	if (live) {
		const script = doc.createElement('script');
		script.textContent = webPictureScript(pictures.base);
		doc.body.append(script);
	}
	return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}

/**
 * Runs inside the page frame: hands Konvo a private channel once, then asks over it for each web
 * file the page shows or plays (now, or as its script sets one) and puts the media proxy address it
 * gets back in place. An address the page's script sets (`img.src`, `setAttribute`, `new Audio`)
 * is held until the proxy address is known, so it never tries the blocked address first, and
 * `play()` waits for it; pictures that arrive in markup (`innerHTML`) are swapped as they appear.
 * Addresses already on the media proxy are left alone; one that can't be had is put back as it was.
 */
export function webPictureScript(base: string): string {
	return `(function(){
var BASE=${JSON.stringify(base)},MAX=${WEB_PICTURES_PER_PAGE},WEB=/^https?:\\/\\/[^\\s]/i,ADMITTED=${ADMITTED.toString()};
var CSS_URL=${CSS_URL.toString()},has=Object.prototype.hasOwnProperty;
var given={},waiting={},count=0,held=new WeakMap();
var channel=new MessageChannel(),port=channel.port1;
parent.postMessage({konvoWebPictures:1},'*',[channel.port2]);
function web(value){
value=String(value==null?'':value).trim();
return WEB.test(value)&&value.indexOf(BASE)!==0&&!ADMITTED.test(value)&&value.length<=2048?value:null;
}
function ask(url,then){
if(has.call(given,url)){then(given[url]);return;}
if(waiting[url]){waiting[url].push(then);return;}
if(count>=MAX){then(null);return;}
count++;waiting[url]=[then];
port.postMessage({konvoWebPicture:url});
}
function askAll(urls,then){
var left=urls.length;
urls.forEach(function(url){ask(url,function(){if(--left===0)then();});});
}
port.onmessage=function(event){
var data=event.data;
if(!data||typeof data.konvoWebPicture!=='string'||!waiting[data.konvoWebPicture])return;
var url=data.konvoWebPicture,src=typeof data.src==='string'&&data.src.indexOf(BASE)===0?data.src:null;
var list=waiting[url];delete waiting[url];given[url]=src;
list.forEach(function(then){try{then(src);}catch(error){}});
};
function candidates(value){
var out=[],at=0,n=value.length,start,url,descriptor;
while(at<n){
while(at<n&&/[\\s,]/.test(value.charAt(at)))at++;
if(at>=n)break;
start=at;while(at<n&&!/\\s/.test(value.charAt(at)))at++;
url=value.slice(start,at);descriptor='';
if(/,$/.test(url))url=url.replace(/,+$/,'');
else{start=at;while(at<n&&value.charAt(at)!==',')at++;descriptor=value.slice(start,at).replace(/^\\s+|\\s+$/g,'');}
out.push([url,descriptor]);
}
return out;
}
function cssUrls(css){
var out=[];
css.replace(CSS_URL,function(match,imported,quote,url){if(!imported&&web(url))out.push(web(url));return match;});
return out;
}
function swapCss(css){
return css.replace(CSS_URL,function(match,imported,quote,url){
var address=!imported&&web(url)&&given[web(url)];
return address?'url("'+address+'")':match;
});
}
function attributes(el){
var name=el.localName;
if(name==='img'||name==='audio'||name==='source')return ['src'];
if(name==='input')return (el.getAttribute('type')||'').toLowerCase()==='image'?['src']:[];
if(name==='video')return ['src','poster'];
if(name==='image')return ['href','xlink:href'];
if(name==='body'||name==='table'||name==='tr'||name==='td'||name==='th')return ['background'];
return [];
}
var setAttribute=Element.prototype.setAttribute;
function check(el){
if(!el||el.nodeType!==1)return;
attributes(el).forEach(function(name){
var value=el.getAttribute(name),url=web(value);
if(url)ask(url,function(src){if(src&&el.getAttribute(name)===value)setAttribute.call(el,name,src);});
});
var srcset=el.getAttribute('srcset');
if(srcset){
var list=candidates(srcset),urls=[];
list.forEach(function(c){if(web(c[0]))urls.push(web(c[0]));});
if(urls.length)askAll(urls,function(){
if(el.getAttribute('srcset')!==srcset)return;
var kept=[];
list.forEach(function(c){var url=web(c[0]),address=url?given[url]:c[0];if(address)kept.push(c[1]?address+' '+c[1]:address);});
setAttribute.call(el,'srcset',kept.join(', '));
});
}
var style=el.getAttribute('style');
if(style){
var styleUrls=cssUrls(style);
if(styleUrls.length)askAll(styleUrls,function(){
var now=el.getAttribute('style');
if(now===style&&swapCss(now)!==now)setAttribute.call(el,'style',swapCss(now));
});
}
if(el.localName==='style'){
var css=el.textContent||'',cssList=cssUrls(css);
if(cssList.length)askAll(cssList,function(){
if(el.textContent===css&&swapCss(css)!==css)el.textContent=swapCss(css);
});
}
}
var SELECTOR='img,source,video,audio,input,image,style,[style],[srcset],[background]';
function scan(node){
if(node.nodeType!==1)return;
check(node);
node.querySelectorAll(SELECTOR).forEach(check);
}
function hold(el,name,url,apply){
var state=held.get(el);
if(!state){state={};held.set(el,state);}
state[name]=url;
ask(url,function(src){
if(state[name]!==url)return;
delete state[name];
apply(src||url);
if(name==='src'&&state.plays){var plays=state.plays;state.plays=null;plays.forEach(function(go){go();});}
});
}
function release(el,name){var state=held.get(el);if(state)delete state[name];}
function setFile(el,name,value,set){
release(el,name);
var url=web(value);
if(!url)return set(value);
if(has.call(given,url))return set(given[url]||url);
hold(el,name,url,set);
}
function patch(type,name){
var proto=type&&type.prototype,own=proto&&Object.getOwnPropertyDescriptor(proto,name);
if(!own||!own.set||!own.configurable)return;
Object.defineProperty(proto,name,{configurable:true,enumerable:own.enumerable,get:own.get,set:function(value){
var el=this;
setFile(el,name,value,function(final){own.set.call(el,final);});
}});
}
patch(window.HTMLImageElement,'src');
patch(window.HTMLSourceElement,'src');
patch(window.HTMLMediaElement,'src');
patch(window.HTMLVideoElement,'poster');
patch(window.HTMLInputElement,'src');
Element.prototype.setAttribute=function(name,value){
var el=this,key=String(name).toLowerCase();
if((key==='src'||key==='poster')&&attributes(el).indexOf(key)>=0)
return setFile(el,key,value,function(final){setAttribute.call(el,name,final);});
return setAttribute.call(el,name,value);
};
var NativeAudio=window.Audio;
if(NativeAudio){
var Audio=function Audio(src){var audio=new NativeAudio();if(src!==undefined)audio.src=src;return audio;};
Audio.prototype=NativeAudio.prototype;
window.Audio=Audio;
}
var media=window.HTMLMediaElement&&HTMLMediaElement.prototype,play=media&&media.play;
if(play)media.play=function(){
var el=this,state=held.get(el);
if(!state||!state.src)return play.apply(el,arguments);
// Asked now too, while the tap that asked still counts (WebKit allows the element to play from then on).
try{var early=play.call(el);if(early&&early.catch)early.catch(function(){});}catch(error){}
return new Promise(function(resolve,reject){
(state.plays||(state.plays=[])).push(function(){Promise.resolve(play.call(el)).then(resolve,reject);});
});
};
scan(document.documentElement);
new MutationObserver(function(records){records.forEach(function(record){
if(record.type==='attributes')check(record.target);
else if(record.type==='characterData')check(record.target.parentNode);
else{if(record.target.localName==='style')check(record.target);record.addedNodes.forEach(scan);}
});}).observe(document.documentElement,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['src','srcset','poster','href','background','style','type']});
})();`;
}

/**
 * Answers the page frame's requests for web files (webPictureScript), gathered and asked for
 * together, with the proxy address or null when there's none. Like the page's own files
 * (HtmlFrame's useLazyAssets), answers go back over the port the frame hands over once, never to
 * the frame window.
 */
export function useWebPictures(
	frame: React.RefObject<HTMLIFrameElement | null>,
	pictures: WebPictures | undefined,
): void {
	useEffect(() => {
		if (!pictures) return;
		const controller = new AbortController();
		const asked = new Set<string>();
		let waiting: Array<string> = [];
		let timer: ReturnType<typeof setTimeout> | null = null;
		let port: MessagePort | null = null;
		const flush = () => {
			timer = null;
			const urls = waiting;
			waiting = [];
			void pictures
				.resolve(urls, controller.signal)
				.catch(() => ({}) as Record<string, string>)
				.then((links) => {
					if (controller.signal.aborted) return;
					for (const url of urls) {
						const src = links[url];
						// The frame puts the page's own address back for the ones it can't have.
						port?.postMessage({konvoWebPicture: url, src: src?.startsWith(pictures.base) ? src : null});
					}
				});
		};
		const onRequest = (event: MessageEvent) => {
			const url = webPictureUrl(String((event.data as {konvoWebPicture?: unknown} | null)?.konvoWebPicture ?? ''));
			if (!url || asked.has(url) || asked.size >= WEB_PICTURES_PER_PAGE) return;
			asked.add(url);
			waiting.push(url);
			timer ??= setTimeout(flush, GATHER_MS);
		};
		const onMessage = (event: MessageEvent) => {
			if (!frame.current || event.source !== frame.current.contentWindow || event.origin !== 'null') return;
			if ((event.data as {konvoWebPictures?: unknown} | null)?.konvoWebPictures !== 1 || !event.ports[0]) return;
			if (port) {
				// One channel per page: a second (another script, or a page the frame went to) gets none.
				event.ports[0].close();
				return;
			}
			port = event.ports[0];
			port.onmessage = onRequest;
		};
		window.addEventListener('message', onMessage);
		return () => {
			controller.abort();
			if (timer) clearTimeout(timer);
			window.removeEventListener('message', onMessage);
			port?.close();
		};
	}, [frame, pictures]);
}
